import { WebOpenMatch } from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import { Pencil, Trash2, Users } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface OpenMatchCardProps {
  match: WebOpenMatch;
  onEdit: () => void;
  onDelete: () => void;
}

export function OpenMatchCard({ match, onEdit, onDelete }: OpenMatchCardProps) {
  const dateTime = new Date(match.dateTime);
  const players = [
    match.playerOneName,
    match.playerTwoName,
    match.playerThreeName,
    match.playerFourName,
  ].filter(Boolean);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} h`;
    return `${hours}h ${mins}min`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg">
              {format(dateTime, "EEEE d 'de' MMMM", { locale: es })}
            </h3>
            <span className="text-sm text-muted-foreground">{format(dateTime, 'HH:mm')}</span>
          </div>

          <div className="space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="font-medium">Cancha:</span>
              <span>{match.court.name}</span>
            </div>

            {match.category && (
              <div className="flex items-center gap-2">
                <span className="font-medium">Categoría:</span>
                <span>{match.category.name}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="font-medium">Duración:</span>
              <span>{formatDuration(match.duration)}</span>
            </div>

            {players.length > 0 && (
              <div className="flex items-start gap-2 mt-2">
                <Users className="size-4 mt-0.5" />
                <div className="flex flex-wrap gap-2">
                  {players.map((player, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs"
                    >
                      {player}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {players.length === 0 && (
              <div className="text-xs text-muted-foreground italic mt-2">
                Sin jugadores asignados
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button onClick={onEdit}>
            <Pencil className="size-4" />
            Editar
          </Button>
          <Button variant="destructive" onClick={onDelete}>
            <Trash2 className="size-4" />
            Eliminar
          </Button>
        </div>
      </div>
    </div>
  );
}
